import { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { AssistantConversation } from "../../api/assistant";
import { assistantConversationQueryOptions, REGISTRATION_FORM_TYPE } from "../../api/assistant";
import { PageSpinner } from "../../shared/Page";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { AssistantChat } from "./AssistantChat";
import { AssistantConversationHeader } from "./AssistantConversationHeader";
import type { AssistantLandingState } from "./navigationState";
import type { AssistantConversationState } from "./navigationState";
import { AssistantLayout } from "./AssistantLayout";
import { recordKinds } from "./assistantConversationMeta";

export const AssistantPage = () => {
  usePrivacyDocumentTitle("Assistant.Title");
  const { paths } = usePrivacyNavigate();
  const { conversationId } = useParams();

  if (!conversationId) return <Navigate to={paths.assistant()} replace />;

  return (
    <AssistantLayout currentConversationId={conversationId}>
      <LoadedConversation key={conversationId} conversationId={conversationId} />
    </AssistantLayout>
  );
};

const LoadedConversation = ({ conversationId }: { conversationId: string }) => {
  const { navigate, paths } = usePrivacyNavigate();
  const location = useLocation();
  const [firstTurn] = useState(() => (location.state as AssistantConversationState)?.firstTurn);
  const conversationQuery = useQuery(assistantConversationQueryOptions(conversationId));

  useEffect(() => {
    if (firstTurn) navigate(paths.assistantConversation(conversationId), { replace: true });
  }, [firstTurn, navigate, paths, conversationId]);

  const conversation = conversationQuery.data ?? (firstTurn ? emptyConversation(conversationId) : undefined);

  if (conversationQuery.isError && !firstTurn) {
    const state: AssistantLandingState = { conversationMissing: true };
    return <Navigate to={paths.assistant()} replace state={state} />;
  }
  if (!conversation) return <PageSpinner />;

  return (
    <>
      <AssistantConversationHeader title={conversation.title} recordKinds={recordKinds(conversation.records)} />
      <AssistantChat conversation={conversation} firstTurn={firstTurn} />
    </>
  );
};

const emptyConversation = (conversationId: string): AssistantConversation => ({
  conversationId,
  formType: REGISTRATION_FORM_TYPE,
  entries: [],
});
